import SignProtocol from './pages/SignProtocol';
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './i18n';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationsProvider } from './contexts/NotificationsContext';
import Layout from './components/layout/Layout';

// Pages
const LoginPage = React.lazy(() => import('./pages/auth/LoginPage'));
const Dashboard = React.lazy(() => import('./pages/admin/Dashboard'));
const VehiclesPage = React.lazy(() => import('./pages/admin/VehiclesPage'));
const VehicleDetail = React.lazy(() => import('./pages/admin/VehicleDetail'));
const UsersPage = React.lazy(() => import('./pages/admin/UsersPage'));
const UserDetail = React.lazy(() => import('./pages/admin/UserDetail'));
const RegionsPage = React.lazy(() => import('./pages/admin/RegionsPage'));
const RepairsPage = React.lazy(() => import('./pages/admin/RepairsPage'));
const FaultsPage = React.lazy(() => import('./pages/admin/FaultsPage'));
const DocumentsPage = React.lazy(() => import('./pages/admin/DocumentsPage'));
const ReportsPage = React.lazy(() => import('./pages/admin/ReportsPage'));
const MessagesPage = React.lazy(() => import('./pages/admin/MessagesPage'));
const EquipmentPage = React.lazy(() => import('./pages/admin/EquipmentPage'));
const AuditPage = React.lazy(() => import('./pages/admin/AuditPage'));
const HistoryPage = React.lazy(() => import('./pages/admin/HistoryPage'));
const PermissionsPage = React.lazy(() => import('./pages/admin/PermissionsPage'));
const NotificationsPage = React.lazy(() => import('./pages/admin/NotificationsPage'));
const ProfilePage = React.lazy(() => import('./pages/admin/ProfilePage'));
const BroadcastPage = React.lazy(() => import('./pages/admin/BroadcastPage'));
const WarehousePage = React.lazy(() => import('./pages/admin/WarehousePage'));

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30000 } }
});

function SetupCheck() {
  React.useEffect(() => {
    fetch('/api/setup/status')
      .then(r => r.json())
      .then(d => { if (d.needsSetup) window.location.href = '/setup-wizard'; })
      .catch(() => {});
  }, []);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="vehicles" element={<VehiclesPage />} />
        <Route path="vehicles/:id" element={<VehicleDetail />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="users/:id" element={<UserDetail />} />
        <Route path="regions" element={<RegionsPage />} />
        <Route path="repairs" element={<RepairsPage />} />
        <Route path="faults" element={<FaultsPage />} />
        <Route path="documents" element={<DocumentsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="equipment" element={<EquipmentPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="history" element={<HistoryPage />} />
        <Route path="permissions" element={<PermissionsPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="broadcast" element={<BroadcastPage />} />
        <Route path="warehouse" element={<WarehousePage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
      <Route path='/sign/:token' element={<SignProtocol/>}/>
</Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SetupCheck />
        <AuthProvider>
          <NotificationsProvider>
            <Suspense fallback={<div className="flex items-center justify-center h-screen text-gray-500">Ładowanie...</div>}>
              <AppRoutes />
            </Suspense>
            <ToastContainer position="top-right" autoClose={4000} />
          </NotificationsProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
